/**
 * Main order status used in DB
 */
export enum OrderStatus {
    STATUS_NEW = 1,
    STATUS_PENDING = 3,
    STATUS_IN_WORK = 2,
    STATUS_CLOSED = 10,
    STATUS_SHIPPED = 11,
}

/**
 * Order status in text form for more convenient usage
 */
export type OrderStatusTxt = 'new' | 'pending' | 'work' | 'closed' | 'shipped';

/**
 * @deprecated Use OrderStatusTxt
 */
export type TOrderStatus = OrderStatusTxt | 'all';

const orderStatusMap: Record<OrderStatusTxt, OrderStatus> = {
    new: OrderStatus.STATUS_NEW,
    pending: OrderStatus.STATUS_PENDING,
    work: OrderStatus.STATUS_IN_WORK,
    closed: OrderStatus.STATUS_CLOSED,
    shipped: OrderStatus.STATUS_SHIPPED,
};

const orderStatusLabel: Record<OrderStatus, string> = {
    [OrderStatus.STATUS_NEW]: 'Новый',
    [OrderStatus.STATUS_PENDING]: 'В очереди',
    [OrderStatus.STATUS_IN_WORK]: 'В работе',
    [OrderStatus.STATUS_CLOSED]: 'На складе',
    [OrderStatus.STATUS_SHIPPED]: 'Отгружен',
};

/**
 * Order status flow
 * from - from what order status the transition is allowed
 * to - to what order status the transition is allowed
 * isForward - is it a forward transition (true) or backward (false)
 *             forward: STATUS_NEW >> STATUS_PENDING >> STATUS_IN_WORK >> STATUS_CLOSED >> STATUS_SHIPPED
 *             backward: STATUS_SHIPPED >> STATUS_CLOSED >> STATUS_IN_WORK >> STATUS_PENDING >> STATUS_NEW
 */
const eligibleOrderStatusTransition: { from: OrderStatus; to: OrderStatus; isForward: boolean }[] = [
    /**
     * TRIGGER: Every new order is set to NEW by default
     * `from: undefined` means creating an order like we did not have neither an order nor it's status
     */
    { from: undefined, to: OrderStatus.STATUS_NEW, isForward: true },

    // TRIGGER: Clicked button "Return to Drafts"
    { from: OrderStatus.STATUS_PENDING, to: OrderStatus.STATUS_NEW, isForward: false },

    /**
     * TRIGGER: Clicked button "Return to Drafts"
     * @see https://lisa10.planfix.ru/task/21978
     */
    { from: OrderStatus.STATUS_IN_WORK, to: OrderStatus.STATUS_NEW, isForward: false },

    // TRIGGER: Order paid OR clicked button "To Work"
    { from: OrderStatus.STATUS_NEW, to: OrderStatus.STATUS_PENDING, isForward: true },

    // TRIGGER: Order stickers printed
    { from: OrderStatus.STATUS_PENDING, to: OrderStatus.STATUS_IN_WORK, isForward: true },

    // TRIGGER: Clicked button "READY"
    { from: OrderStatus.STATUS_IN_WORK, to: OrderStatus.STATUS_CLOSED, isForward: true },

    // TRIGGER: Clicked button "SHIP"
    { from: OrderStatus.STATUS_CLOSED, to: OrderStatus.STATUS_SHIPPED, isForward: true },
];

export function orderStatusToLabel(status: OrderStatus): string {
    return orderStatusLabel[status];
}

export function orderCanChangeStatus(from: OrderStatus, to: OrderStatus, forwardOnly?: boolean): boolean {
    return eligibleOrderStatusTransition.some(transition => transition.from === from && transition.to === to && (!forwardOnly || transition.isForward));
}

export function orderGetEligibleFromStatuses(statusTo: OrderStatus, forwardOnly?: boolean): OrderStatus[] {
    return eligibleOrderStatusTransition
        .filter(transition => transition.to === statusTo && transition.from && (!forwardOnly || transition.isForward))
        .map(transition => transition.from);
}

/**
 * Returns eligible "from" statuses formatted as a single human-readable string,
 * e.g. `«В очереди»` or `«В очереди» или «В работе»`.
 */
export function orderGetEligibleFromStatusLabels(statusTo: OrderStatus, forwardOnly?: boolean): string {
    return orderStatusesToLabels(orderGetEligibleFromStatuses(statusTo, forwardOnly)).map(status => `«${status}»`).join(' или ');
}

export function orderStatusesToLabels(statusList: OrderStatus[]): string[] {
    return statusList.map(status => orderStatusToLabel(status));
}

export function orderStatusFrom(value: OrderStatusTxt): OrderStatus {
    return orderStatusMap[value];
}

export function orderStatusListFrom(list: OrderStatusTxt[]): OrderStatus[] {
    return Array.isArray(list)
        ? list.flatMap<OrderStatus>(value => (orderStatusMap[value] ? orderStatusMap[value] : []))
        : [];
}
